import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { listSessions } from "../lib/api";

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listSessions()
      .then((s) => setSessions(s))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>
        <div className="font-display font-semibold text-slate-900">Past Calls</div>
        <div />
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="sessions-page">
        {loading && <div className="text-slate-500">Loading…</div>}

        {!loading && sessions.length === 0 && (
          <div
            className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500"
            data-testid="no-sessions"
          >
            No calls yet. Run your first call from the dashboard.
          </div>
        )}

        <ul className="space-y-3">
          {sessions.map((s) => (
            <li key={s.id}>
              <Link
                to={`/sessions/${s.id}/summary`}
                data-testid={`session-row-${s.id}`}
                className="block bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-400 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display font-semibold text-slate-900">{s.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {new Date(s.started_at).toLocaleString()} • status:{" "}
                      <span className="font-mono">{s.status}</span>
                    </div>
                  </div>
                  <FileText className="h-5 w-5 text-slate-400" />
                </div>
                <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                  <Badge tone="orange" n={s.pain_points?.length} label="pain" />
                  <Badge tone="emerald" n={s.goals?.length} label="goals" />
                  <Badge tone="amber" n={s.cost_of_inaction?.length} label="cost" />
                  <Badge tone="blue" n={s.decision_maker?.length} label="DM" />
                  <Badge tone="yellow" n={s.marked_moments?.length} label="marked" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

function Badge({ tone, n, label }) {
  const map = {
    orange: "bg-orange-50 text-orange-800 border-orange-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    yellow: "bg-yellow-50 text-yellow-900 border-yellow-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 ${map[tone]}`}>
      <span className="font-mono">{n ?? 0}</span>
      <span className="opacity-70">{label}</span>
    </span>
  );
}
